import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Menu, Trash2, Shield, User, ArrowLeft, KeyRound, ChevronRight, ChevronLeft, Search } from "lucide-react";
import Header from "../components/Header";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import ResetPasswordModal from "./components/ResetPasswordModal";
import BulkUploadModal from "./components/BulkUploadModal";
import api from "../connector/api";

const UserInfo = () => {
  const { id } = useParams();
  const userId = parseInt(id);
  const navigate = useNavigate();
  
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    is_active: false,
  });
  
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [tempAvailable, setTempAvailable] = useState([]);
  const [tempSelected, setTempSelected] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isBulkOpen, setBulkOpen] = useState(false);
  
  // Local state for searching/filtering lists inside permission cards
  const [permSearchQuery, setPermSearchQuery] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("users/");
        
        // 🛡️ Safely extract the array whether it's paginated or a raw list
        let rawUsers = [];
        if (Array.isArray(res.data)) {
          rawUsers = res.data;
        } else if (res.data && Array.isArray(res.data.results)) {
          rawUsers = res.data.results; // Fix for Django REST framework pagination wrapper
        } else {
          console.warn("Unexpected data structure from users payload:", res.data);
        }

        const normalized = rawUsers.map((u) => ({
          id: u.id,
          username: u.username,
          email: u.email,
          first_name: u.first_name || u.employee?.first_name || "",
          last_name: u.last_name || u.employee?.last_name || "",
          status: u.employee?.status || "Inactive",
          is_active: u.is_active || false,
          permissions: u.permissions || [],
        }));
        setUsers(normalized);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const res = await api.get("custom-permissions/");
        
        // 🛡️ Safe check: look inside res.data.results if it's an object wrapper
        let permissionsArray = [];
        
        if (Array.isArray(res.data)) {
          permissionsArray = res.data;
        } else if (res.data && Array.isArray(res.data.results)) {
          permissionsArray = res.data.results; // Extract from Django's pagination wrapper
        } else {
          console.warn("Could not find array in custom-permissions payload:", res.data);
        }

        setAvailablePermissions(permissionsArray);
      } catch (err) {
        console.error("Failed to fetch permissions layout:", err);
        setAvailablePermissions([]);
      }
    };
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchUser = async () => {
      try {
        const res = await api.get(`users/${userId}/`);
        const user = res.data;

        setFormData({
          username: user.username || "",
          first_name: user.first_name || "",
          last_name: user.last_name || "",
          email: user.email || "",
          is_active: Boolean(user.is_active),
        });

        setSelectedPermissions(user.permissions || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchUser();
  }, [userId]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectPermission = (id, fromAvailable) => {
    if (fromAvailable) {
      setTempAvailable(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    } else {
      setTempSelected(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    }
  };

  const moveOptions = (toSelected) => {
    if (toSelected) {
      const moving = unassignedPermissions.filter((p) => tempAvailable.includes(p.id));
      setSelectedPermissions([...selectedPermissions, ...moving]);
      setTempAvailable([]);
    } else {
      const moving = selectedPermissions.filter((p) => tempSelected.includes(p.id));
      setSelectedPermissions(selectedPermissions.filter((p) => !tempSelected.includes(p.id)));
      setTempSelected([]);
    }
  };

  const moveOptionById = (id, fromAvailable) => {
    if (fromAvailable) {
      const moving = unassignedPermissions.find((p) => p.id === id);
      if (!moving) return;
      setSelectedPermissions([...selectedPermissions, moving]);
      setTempAvailable(prev => prev.filter(item => item !== id));
    } else {
      setSelectedPermissions(selectedPermissions.filter((p) => p.id !== id));
      setTempSelected(prev => prev.filter(item => item !== id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`users/${userId}/`, {
        ...formData,
        custom_permissions: selectedPermissions.map((p) => p.id),
      });
      toast.success("User configuration saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update system user.");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`users/${userId}/`);
      toast.success("User deleted successfully!");
      setTimeout(() => navigate("/users"), 500);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete user mapping.");
    }
  };

  // 1. Safe extraction that normalizes whatever the user model has assigned
  const assignedList = Array.isArray(selectedPermissions) ? selectedPermissions : [];
  const basePoolList = Array.isArray(availablePermissions) ? availablePermissions : [];

  // 2. Safe Dynamic Pool Extraction checking BOTH 'id' and 'codename' parameters
  const unassignedPermissions = basePoolList.filter((availItem) => {
    if (!availItem) return false;
    
    return !assignedList.some((selectedItem) => {
      if (!selectedItem) return false;

      // Handle object comparison by ID
      if (selectedItem.id && availItem.id) {
        return String(selectedItem.id) === String(availItem.id);
      }
      
      // Fallback matching comparison by unique codename string identifier
      if (selectedItem.codename && availItem.codename) {
        return selectedItem.codename === availItem.codename;
      }
      
      // Fallback if user array is just an explicit array of codename strings
      if (typeof selectedItem === "string" && availItem.codename) {
        return selectedItem === availItem.codename;
      }

      return false;
    });
  });

  // 3. Keep search filter execution identical matching the computed dynamic unassigned pool
  const filteredAvailable = unassignedPermissions.filter(p => 
    p?.name?.toLowerCase().includes(permSearchQuery.toLowerCase()) || 
    p?.codename?.toLowerCase().includes(permSearchQuery.toLowerCase())
  );

  const filteredSelected = assignedList.filter(p => {
    // If the assigned item is a raw string, normalize it into an object representation on the fly for safe UI reading
    const nameStr = typeof p === "string" ? p : p?.name || "";
    const codeStr = typeof p === "string" ? p : p?.codename || "";
    
    return nameStr.toLowerCase().includes(permSearchQuery.toLowerCase()) || 
           codeStr.toLowerCase().includes(permSearchQuery.toLowerCase());
  });
  
  if (!userId) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="relative flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#002855]" />
          <span className="absolute text-[10px] font-bold text-[#002855] animate-pulse">26</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden font-dynamic-title bg-slate-50">
      <Header />
      <Toaster position="top-right" />

      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6 sm:p-8 max-w-7xl w-full mx-auto space-y-6">
          
          {/* Main Action Top Bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => navigate("/users")}
                className="p-2 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div>
                <h1 className="text-base font-bold text-slate-900">
                  {formData.last_name || formData.first_name ? `${formData.last_name}, ${formData.first_name}` : "Profile Ledger"}
                </h1>
                <p className="text-[11px] font-mono text-slate-400 mt-0.5">UID-{userId} // Account Identification Parameters</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 rounded-xl shadow-2xs transition-all"
                onClick={() => setShowResetModal(true)}
              >
                <KeyRound className="w-3.5 h-3.5 text-slate-400" />
                Reset Password
              </button>
              <button
                type="button"
                className="p-2 border border-rose-200 bg-rose-50/50 hover:bg-rose-50 text-rose-600 rounded-xl transition-all shadow-2xs"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Form Control Layer */}
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Box Module: Personal Information */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-2xs overflow-hidden">
              <div className="border-b border-slate-100 bg-slate-50/50 px-5 py-3.5 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Personal Information Parameters
                </h3>
              </div>
              
              <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3.5 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3.5 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="name@corporate.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3.5 text-xs text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all shadow-2xs"
                  />
                </div>
              </div>
            </div>

            {/* Box Module: Access Matrix & Permissions */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-2xs overflow-hidden">
              <div className="border-b border-slate-100 bg-slate-50/50 px-5 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    System Permissions Matrix
                  </h3>
                </div>
                
                {/* Active Status Control Toggled on Header right */}
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="w-3.5 h-3.5 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-400 accent-slate-900"
                  />
                  <span className="text-xs font-semibold text-slate-700">Account Active State</span>
                </label>
              </div>

              <div className="p-5 space-y-4">
                <p className="text-xs text-slate-400 leading-relaxed max-w-3xl">
                  Configure explicit permission credentials down below. De-activating the profile locks out operations instantly without terminating historical ledger entry connections.
                </p>

                {/* Internal Search bar for searching specific codes inside long columns */}
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search matrix definitions..."
                    value={permSearchQuery}
                    onChange={(e) => setPermSearchQuery(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl py-1.5 pl-9 pr-3 text-[11px] text-slate-800 placeholder-slate-400 focus:outline-none shadow-2xs"
                  />
                </div>

                {/* Refactored Dual Column Layout Area */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_48px_1fr] gap-4 pt-1">
                  
                  {/* Left Column Box Container: Available Keys */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden flex flex-col bg-white">
                    <div className="bg-slate-50/50 border-b border-slate-100 p-3">
                      <h4 className="text-xs font-bold text-slate-700">Available Permissions</h4>
                      <p className="text-[10px] text-slate-400 mt-0.5">Double click key item to move immediately.</p>
                    </div>
                    <div className="p-2 h-52 overflow-y-auto space-y-1 scrollbar-thin">
                      {filteredAvailable.length > 0 ? filteredAvailable.map((p) => (
                        <div
                          key={p.id}
                          onDoubleClick={() => moveOptionById(p.id, true)}
                          onClick={() => handleSelectPermission(p.id, true)}
                          className={`px-3 py-2 text-xs rounded-lg border transition-all cursor-pointer font-medium ${
                            tempAvailable.includes(p.id)
                              ? "bg-[#002855] bg-opacity-80 border-[#002855] text-white"
                              : "bg-white border-slate-100 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <div className="font-semibold text-[11px]">{p.name}</div>
                          <div className={`text-[10px] font-mono mt-0.5 ${tempAvailable.includes(p.id) ? "text-slate-300" : "text-slate-400"}`}>{p.codename}</div>
                        </div>
                      )) : (
                        <div className="text-[11px] text-slate-400 text-center py-12 font-medium">No available permissions found</div>
                      )}
                    </div>
                  </div>

                  {/* Mid Column Toggles: Core Intersect Buttons */}
                  <div className="flex flex-row md:flex-col justify-center items-center gap-2">
                    <button
                      type="button"
                      onClick={() => moveOptions(true)}
                      disabled={tempAvailable.length === 0}
                      className="p-2 bg-[#002855] bg-opacity-80 text-white rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-2xs hover:bg-opacity-95"
                    >
                      <ChevronRight className="w-4 h-4 hidden md:block" />
                      <span className="text-xs font-bold px-3 md:hidden">Add</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveOptions(false)}
                      disabled={tempSelected.length === 0}
                      className="p-2 bg-slate-900 text-white rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-2xs hover:bg-slate-800"
                    >
                      <ChevronLeft className="w-4 h-4 hidden md:block" />
                      <span className="text-xs font-bold px-3 md:hidden">Remove</span>
                    </button>
                  </div>

                  {/* Right Column Box Container: Active Authorized Keys */}
                  <div className="border border-[#002855] rounded-xl overflow-hidden flex flex-col bg-white">
                    <div className="bg-[#002855] bg-opacity-80 border-b border-[#002855] p-3">
                      <h4 className="text-xs font-bold text-white">Assigned Permissions</h4>
                      <p className="text-[10px] text-slate-300/70 mt-0.5">Assigned rules configurations applied to this profile.</p>
                    </div>
                    <div className="p-2 h-52 overflow-y-auto space-y-1 scrollbar-thin">
                      {filteredSelected.length > 0 ? filteredSelected.map((p) => (
                        <div
                          key={p.id}
                          onDoubleClick={() => moveOptionById(p.id, false)}
                          onClick={() => handleSelectPermission(p.id, false)}
                          className={`px-3 py-2 text-xs rounded-lg border transition-all cursor-pointer font-medium ${
                            tempSelected.includes(p.id)
                              ? "bg-slate-100 border-slate-200 text-slate-900"
                              : "bg-white border-slate-100 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <div className="font-semibold text-[11px]">{p.name}</div>
                          <div className="text-[10px] font-mono text-slate-400 mt-0.5">{p.codename}</div>
                        </div>
                      )) : (
                        <div className="text-[11px] text-slate-400 text-center py-12 font-medium">No permission assigned</div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Bottom Anchor Actions Panel */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="px-5 py-2.5 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-xs font-semibold text-white rounded-xl transition-all shadow-2xs"
              >
                Save System Ledger Updates
              </button>
            </div>
          </form>

        </main>

        <ResetPasswordModal
          show={showResetModal}
          onClose={() => setShowResetModal(false)}
          userId={userId}
        />

        <DeleteConfirmModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          userName={`${formData.first_name} ${formData.last_name}`}
        />

        <BulkUploadModal
          isOpen={isBulkOpen}
          onClose={() => setBulkOpen(false)}
          onUsersUploaded={(newUsers) => setUsers((prev) => [...prev, ...newUsers])}
        />
      </div>
    </div>
  );
};

export default UserInfo;